import {
  Button,
  Container,
  Fade,
  Grid,
  IconButton,
  Modal,
} from '@material-ui/core'
import {
  Apps,
  Close,
  FormatListBulleted,
  ImportExport,
  Search,
} from '@material-ui/icons'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteMachine,
  getMachinesByUserId,
} from '../../../actions/machineActions'
import filterIcon from '../../../assets/img/icons/filter.svg'
import {
  MACHINE_CREATE_RESET,
  MACHINE_DELETE_RESET,
  MACHINE_UPDATE_RESET,
} from '../../../constants/machineConstants'
import { SIDE_NAV_CLOSE } from '../../../constants/navigationConstants'
import machineList from '../../../data/machines'
import { useInput } from '../../../hooks/useInput'
import ActionsMenu from '../../ActionsMenu/ActionsMenu'
import AutoCompleteSelect from '../../Form/AutoCompleteSelect'
import SearchInput from '../../Form/SearchInput'
import Loader from '../../Loader/Loader'
import AddEditMachine from '../../Machine/AddEditMachine'
import Message from '../../Message/Message'
import ToastMessage from '../../ToastMessage/ToastMessage'
import MachineGrid from './MachineGrid'
import MachineTable from './MachineTable'

const MachineLibrary = () => {
  const dispatch = useDispatch()

  const { value: model, setValue: setModel } = useInput('')
  const { bind: bindSearch, value: searchValue } = useInput('')
  const [viewType, setViewType] = useState('table')
  const [showFilters, setShowFilters] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [showAddEditMachine, setShowAddEditMachine] = useState(false)
  const [machineToEdit, setMachineToEdit] = useState('')
  const [addMachineToast, setAddMachineToast] = useState(false)
  const [updateMachineToast, setUpdateMachineToast] = useState(false)
  const [updatedMachineName, setUpdatedMachineName] = useState('')
  const [createMachineName, setCreateMachineName] = useState('')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [machineToDelete, setMachineToDelete] = useState('')
  const [deleteMachineToast, setDeleteMachineToast] = useState(false)
  const [filteredMachines, setFilteredMachines] = useState([])
  const [sortValue, setSortValue] = useState(false)
  // eslint-disable-next-line no-unused-vars
  const [sorting, setSorting] = useState(false)

  const { userInfo } = useSelector((state) => state.userLogin)
  const { loading, error, machines } = useSelector((state) => state.machineList)
  const { createdMachine, success: createMachineSuccess } = useSelector(
    (state) => state.machineCreate
  )
  const { success: machineUpdateSuccess, machine: updatedMachine } =
    useSelector((state) => state.machineUpdate)
  const { success: successDelete } = useSelector((state) => state.machineDelete)

  useEffect(() => {
    if (machines) {
      setFilteredMachines(machines)
    }
  }, [dispatch, machines])

  useEffect(() => {
    if (model !== '') {
      setFilteredMachines(machines.filter((x) => x.model === model))
    } else {
      setFilteredMachines(machines)
    }
  }, [model, setFilteredMachines, machines])

  useEffect(() => {
    if (searchValue !== '') {
      setFilteredMachines(
        machines.filter((x) =>
          x.name.toLowerCase().includes(searchValue.toLowerCase())
        )
      )
    } else {
      setFilteredMachines(machines)
    }
  }, [searchValue, setFilteredMachines, machines])

  useEffect(() => {
    setSorting(true)

    if (sortValue === 'az') {
      setFilteredMachines(
        filteredMachines.sort((a, b) => a.name.localeCompare(b.name))
      )
    }

    if (sortValue === 'za') {
      setFilteredMachines(
        filteredMachines.sort((a, b) => b.name.localeCompare(a.name))
      )
    }

    if (sortValue === 'lastUpdated') {
      setFilteredMachines(
        filteredMachines.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
      )
    }

    if (sortValue === 'newest') {
      setFilteredMachines(
        filteredMachines.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
      )
    }

    setTimeout(() => setSorting(false), 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setFilteredMachines, sortValue, setSorting])

  useEffect(() => {
    dispatch(getMachinesByUserId(userInfo._id))
    dispatch({ type: SIDE_NAV_CLOSE })

    if (createMachineSuccess) {
      window.scrollTo(0, 0)
      setAddMachineToast(true)
      setCreateMachineName(createdMachine.name)
      dispatch({ type: MACHINE_CREATE_RESET })
    }

    if (machineUpdateSuccess) {
      window.scrollTo(0, 0)
      setUpdateMachineToast(true)
      setUpdatedMachineName(updatedMachine.name)
      setMachineToEdit('')
      dispatch({ type: MACHINE_UPDATE_RESET })
    }

    if (successDelete) {
      window.scrollTo(0, 0)
      dispatch(getMachinesByUserId(userInfo._id))
      setDeleteMachineToast(true)
      dispatch({ type: MACHINE_DELETE_RESET })
    }
  }, [
    updatedMachine,
    createdMachine,
    createMachineSuccess,
    setUpdateMachineToast,
    setUpdatedMachineName,
    dispatch,
    machineUpdateSuccess,
    successDelete,
    userInfo,
  ])

  const handleClear = () => {
    setModel('')
  }

  const handleClose = () => {
    setMachineToEdit('')
    setShowAddEditMachine(false)
  }

  const confirmDeleteMachine = () => {
    dispatch(deleteMachine(machineToDelete._id))
    setShowDeleteModal(false)
  }

  return (
    <div className="library gray-bg">
      <Container className="p-b-100">
        <h3 className="page-title accent-left">Machine library</h3>

        <div className="flex flex-end m-b-16" style={{ height: 33 }}>
          {showSearch ? (
            <SearchInput bindValue={bindSearch} setShowSearch={setShowSearch} />
          ) : (
            <IconButton onClick={() => setShowSearch(true)}>
              <Search />
            </IconButton>
          )}
          <ActionsMenu
            icon={<ImportExport />}
            options={[
              { onClick: () => setSortValue('az'), label: 'A - Z' },
              { onClick: () => setSortValue('za'), label: 'Z - A' },
              { onClick: () => setSortValue('newest'), label: 'Newest' },
              {
                onClick: () => setSortValue('lastUpdated'),
                label: 'Last updated',
              },
            ]}
          ></ActionsMenu>

          <IconButton
            className="m-l-5"
            style={{ width: 32, height: 33 }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <img style={{ width: 20 }} src={filterIcon} alt="Filter" />
          </IconButton>
          {viewType === 'table' ? (
            <IconButton className="m-l-5" onClick={() => setViewType('grid')}>
              <Apps />
            </IconButton>
          ) : (
            <IconButton className="m-l-5" onClick={() => setViewType('table')}>
              <FormatListBulleted />
            </IconButton>
          )}
          <Button
            className="m-l-24"
            variant="contained"
            color="primary"
            onClick={() => setShowAddEditMachine(true)}
          >
            Add machine
          </Button>
        </div>

        {showFilters && (
          <div className="filter-bar">
            <div className="flex space-between">
              <label className="fs14 strong">Filters</label>
              <span
                className="link fs12"
                rel="noopener noreferrer"
                target="_blank"
                onClick={handleClear}
              >
                Clear
              </span>
            </div>
            <div className="form-container p-l-0 p-r-0">
              <Grid container spacing={2}>
                <Grid item xs={3}>
                  <AutoCompleteSelect
                    label="Model"
                    name="model"
                    options={machineList}
                    value={model}
                    setValue={setModel}
                    minWidth={340}
                  />
                </Grid>
              </Grid>
            </div>
          </div>
        )}

        {loading && <Loader />}

        {error && <Message variant="error">{error}</Message>}

        {!loading && !error && viewType === 'table' ? (
          <MachineTable
            machines={filteredMachines}
            setMachineToEdit={setMachineToEdit}
            setShowAddEditMachine={setShowAddEditMachine}
            setMachineToDelete={setMachineToDelete}
            setShowDeleteModal={setShowDeleteModal}
          />
        ) : (
          <MachineGrid
            machines={filteredMachines}
            setMachineToEdit={setMachineToEdit}
            setShowAddEditMachine={setShowAddEditMachine}
            setMachineToDelete={setMachineToDelete}
            setShowDeleteModal={setShowDeleteModal}
          />
        )}
      </Container>

      <Modal open={showAddEditMachine} onClose={handleClose}>
        <Fade in={showAddEditMachine}>
          <div className="modal-window full">
            <div className="modal-header">
              <div>{machineToEdit === '' ? 'Add new' : 'Edit'} machine</div>
              <IconButton onClick={handleClose}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <AddEditMachine
                setShowAddMachine={setShowAddEditMachine}
                setMachineToEdit={setMachineToEdit}
                machineToEdit={machineToEdit}
              />
            </div>
          </div>
        </Fade>
      </Modal>

      <Modal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <Fade in={showDeleteModal}>
          <div className="modal-window">
            <div className="modal-header">
              <div>Delete machine?</div>
              <IconButton onClick={() => setShowDeleteModal(false)}>
                <Close />
              </IconButton>
            </div>
            <div className="modal-body">
              <h4 className="m-b-32">Delete machine?</h4>
              <p>
                Are you sure you want to delete {machineToDelete.name}? This may
                impact any projects that are currently using this machine.
              </p>
              <div className="actions m-t-40 flex-end flex">
                <Button
                  color="primary"
                  variant="outlined"
                  className="m-r-24"
                  onClick={() => setShowDeleteModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={confirmDeleteMachine}
                >
                  Confirm
                </Button>
              </div>
            </div>
          </div>
        </Fade>
      </Modal>

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Machine added</p>
            <p className="fs14 m-b-0">
              You successfully created {createMachineName}.
            </p>
          </>
        }
        open={addMachineToast}
        setOpen={setAddMachineToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Machine updated</p>
            <p className="fs14 m-b-0">
              You successfully updated {updatedMachineName}.
            </p>
          </>
        }
        open={updateMachineToast}
        setOpen={setUpdateMachineToast}
      />

      <ToastMessage
        message={
          <>
            <p className="fs16 fw600">Machine deleted</p>
            <p className="fs14 m-b-0">
              You successfully deleted {machineToDelete.name}.
            </p>
          </>
        }
        open={deleteMachineToast}
        setOpen={setDeleteMachineToast}
      />
    </div>
  )
}

export default MachineLibrary
